import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';
import { colors } from './colors';
import { SemanticTheme, semanticTheme } from './semantic-theme';
import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    brandBlue: Palette['primary'];
    primaryMint: Palette['primary'];
    amber: Record<number, string>;
    drawerBlue: Palette['primary'];
    semantic: SemanticTheme['light' | 'dark'];
  }
  interface PaletteOptions {
    brandBlue?: PaletteOptions['primary'];
    primaryMint?: PaletteOptions['primary'];
    amber: Record<number, string>;
    drawerBlue: PaletteOptions['primary'];
    semantic?: SemanticTheme['light' | 'dark'];
  }
}

const statCardColors = {
  violet: {
    50: '#F4F2FF',
    100: '#E8E4FF',
    200: '#D2CBFA',
    300: '#B7AEF2',
    400: '#9F93EC',
    500: '#8B7FE8',
    600: '#7467D1',
    700: '#5E53B5',
    800: '#4A4097',
    900: '#38317A',
  },
  slateBlue: {
    50: '#F2F4FF',
    100: '#E3E7FC',
    200: '#C9D0F6',
    300: '#A8B1EC',
    400: '#8893E1',
    500: '#6E7BD1',
    600: '#5965B7',
    700: '#475198',
    800: '#37407A',
    900: '#2A315E',
  },
  cyan: {
    50: '#F0FAFD',
    100: '#DCF3F9',
    200: '#BDE7F2',
    300: '#97D8E7',
    400: '#74CBE0',
    500: '#55BFD6',
    600: '#4698AC',
    700: '#357F92',
    800: '#2A6574',
    900: '#1F4842',
  },
  teal: {
    50: '#EFF9F8',
    100: '#D6F0EF',
    200: '#AFE0DE',
    300: '#84CECB',
    400: '#5BBAB8',
    500: '#3FA7A6',
    600: '#328B89',
    700: '#28716F',
    800: '#205A59',
    900: '#194645',
  },
  orange: {
    50: '#FFF7EB',
    100: '#FDEBD1',
    200: '#FAD6A3',
    300: '#F5BE75',
    400: '#EEAA55',
    500: '#E9A94C',
    600: '#C78B3E',
    700: '#9F6F32',
    800: '#7C5627',
    900: '#5F390A',
  },
  rose: {
    50: '#FFF1F1',
    100: '#FBE2E2',
    200: '#F5C7C7',
    300: '#EDAAAA',
    400: '#E39696',
    500: '#D98989',
    600: '#BC6F6F',
    700: '#9A5959',
    800: '#7A4545',
    900: '#5D2525',
  },
  green: {
    50: '#EAFBF2',
    100: '#C2ECDA',
    200: '#9EDFC0',
    300: '#79D2A6',
    400: '#3FA76D',
    500: '#217A49',
    600: '#1C6A40',
    700: '#175937',
    800: '#12472C',
    900: '#0D3620',
  },
  gray: {
    50: '#FAFAFA',
    100: '#F2F2F2',
    200: '#E0E0E0',
    300: '#C2C2C2',
    400: '#5C5C5C',
    500: '#4D4D4D',
    600: '#3D3D3D',
    700: '#333333',
    800: '#262626',
    900: '#1A1A1A',
  },
} as const;

const otherColors = {
  amber: {
    100: '#FEF9E7',
    200: '#FDEFC0',
    300: '#FAE088',
    400: '#F5CB47',
    500: '#E6A817',
    600: '#C48A0C',
    700: '#9E6D07',
    800: '#785204',
    900: '#523802',
  },
} as const;

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    background: {
      default: semanticTheme.light.surface.bg2,
      paper: semanticTheme.light.surface.bg1,
    },
    primary: {
      light: colors.primary[100],
      main: colors.primary[700],
      dark: colors.primary[800],
      contrastText: colors.white,
    },
    secondary: {
      main: colors.accent[500],
    },
    success: {
      light: colors.success[100],
      main: colors.success[500],
      dark: colors.success[700],
    },
    warning: {
      light: colors.warning[100],
      main: colors.warning[500],
      dark: colors.warning[700],
    },
    error: {
      light: colors.error[100],
      main: colors.error[500],
      dark: colors.error[700],
    },

    info: {
      light: colors.info[100],
      main: colors.info[500],
      dark: colors.info[700],
    },

    text: {
      primary: semanticTheme.light.text.body,
      secondary: semanticTheme.light.text.secondary,
      disabled: semanticTheme.light.text.disabled,
    },
    grey: colors.grey,
    brandBlue: {
      main: '#233160',
      light: '#EDEFF3',
      dark: '#1e2b54',
      contrastText: '#fff',
    },
    drawerBlue: {
      main: '#233160',
      light: '#EDEFF3',
      dark: '#1e2b54',
      contrastText: '#fff',
    },
    primaryMint: {
      main: '#6FC6AC',
      light: '#F3FAF8',
      dark: '#4D8A78',
      contrastText: '#fff',
    },
    colors,
    semantic: semanticTheme.light,
    ...statCardColors,
    ...otherColors,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => {
        const scrollbar = theme.palette.semantic.scrollbar;

        return {
          '*::-webkit-scrollbar-track': {
            backgroundColor: scrollbar.default.bg,
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: scrollbar.default.toggle,
            '&:hover': {
              backgroundColor: scrollbar.default.hover,
            },
          },
          '*::-webkit-scrollbar-corner': {
            backgroundColor: scrollbar.default.bg,
          },
          '*': {
            scrollbarColor: `${scrollbar.default.toggle} ${scrollbar.default.bg}`,
          },
        };
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          backgroundColor: semanticTheme.light.surface.bg2,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          '&:has(.MuiAvatar-fallback)': {
            backgroundColor: semanticTheme.light.avatars.default,
            color: semanticTheme.light.avatars.contrastText,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiDataGrid-toolbar': {
            borderBottom: 'none',
          },
          '& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader': {
            borderBottom: 'none',
          },
          border: 'none',
          boxShadow: '0 1px 6px 0 #0000001F',
          borderRadius: '8px',
          backgroundColor: theme.palette.background.paper,
        }),
        columnHeaderTitle: {
          fontWeight: 'bold',
        },
        cell: {
          ':focus-within': {
            outline: 'none',
          },
        },
        filterForm: {
          gap: 8,
          alignItems: 'baseline',
        },
        detailPanel: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
      },
      defaultProps: {
        resizeThrottleMs: 8,
        slotProps: {
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
        },
      },
    },
    MuiCard: {
      defaultProps: {
        // The default props to change
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          background: 'white',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        // The default props to change
        size: 'small',
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          '& .MuiList-root': {
            padding: 8,
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: () => ({
          '&.Mui-selected': {
            color: semanticTheme.light.button.primary.background,
            backgroundColor: semanticTheme.light.button.secondary.hover,
            borderRadius: 4,
          },

          '&.Mui-selected:hover': {
            color: semanticTheme.light.button.primary.background,
            backgroundColor: semanticTheme.light.button.secondary.hover,
            borderRadius: 4,
          },
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: {
          padding: 8,
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            color: semanticTheme.light.button.primary.background,
            backgroundColor: semanticTheme.light.button.secondary.hover,
            borderRadius: 4,
          },

          '& .MuiAutocomplete-option[aria-selected="true"]:hover': {
            color: semanticTheme.light.button.primary.background,
            backgroundColor: semanticTheme.light.button.secondary.hover,
            borderRadius: 4,
          },
        },
      },
    },
    MuiPickersTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          // ':hover': {
          //   backgroundColor: '#D4EEE6',
          // },
          '&.Mui-selected': {
            '&:hover': {
              backgroundColor: '#EEF1FA',
            },
            backgroundColor: theme.palette.primary.light,
          },
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: () => ({
          border: 'none',
          backgroundColor: semanticTheme.light.surface.bg2,
          '--Paper-overlay': 'unset',
          backgroundImage: 'unset',
        }),
      },
    },
    MuiAccordion: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${theme.palette.grey[200]}`,
        }),
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.grey[200],
        }),
      },
    },
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: semanticTheme.dark.surface.bg3,
      paper: semanticTheme.dark.surface.bg1,
    },
    primary: {
      light: colors.primary[400],
      main: colors.primary[600],
      dark: colors.primary[800],
      contrastText: colors.white,
    },

    secondary: {
      main: colors.accent[400],
    },

    success: {
      light: colors.success[300],
      main: colors.success[400],
      dark: colors.success[600],
    },

    warning: {
      light: colors.warning[300],
      main: colors.warning[400],
      dark: colors.warning[600],
    },

    error: {
      light: colors.error[300],
      main: colors.error[400],
      dark: colors.error[600],
    },

    info: {
      light: colors.info[300],
      main: colors.info[400],
      dark: colors.info[600],
    },

    text: {
      primary: semanticTheme.dark.text.body,
      secondary: semanticTheme.dark.text.secondary,
      disabled: semanticTheme.dark.text.disabled,
    },

    grey: colors.grey,
    brandBlue: {
      main: '#05D7D1',
      light: '#1E2232',
      dark: '#04a19c',
      contrastText: '#fff',
    },
    drawerBlue: {
      main: colors.darkBlue[6],
      light: colors.darkBlue[3],
      dark: colors.darkBlue[8],
      contrastText: '#fff',
    },
    primaryMint: {
      main: '#009c77',
      light: '#0F2822',
      dark: '#006c52',
      contrastText: '#fff',
    },
    colors,
    semantic: semanticTheme.dark,
    ...statCardColors,
    ...otherColors,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => {
        const scrollbar = theme.palette.semantic.scrollbar;

        return {
          '*::-webkit-scrollbar-track': {
            backgroundColor: scrollbar.default.bg,
          },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: scrollbar.default.toggle,
            '&:hover': {
              backgroundColor: scrollbar.default.hover,
            },
          },
          '*::-webkit-scrollbar-corner': {
            backgroundColor: scrollbar.default.bg,
          },
          '*': {
            scrollbarColor: `${scrollbar.default.toggle} ${scrollbar.default.bg}`,
          },
        };
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          backgroundColor: semanticTheme.dark.surface.bg3,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          color: colors.neutral[200],
          '&.Mui-selected': {
            color: colors.info[400],
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        // The default props to change
        size: 'small',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.common.white,
          },

          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
            {
              borderColor: theme.palette.primary.main,
              borderWidth: 2,
            },

          '& .MuiInputLabel-root.Mui-focused': {
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          '& .MuiList-root': {
            padding: 8,
          },
        },
        paper: () => ({
          backgroundColor: semanticTheme.dark.surface.bg2,
          backgroundImage: 'none',
        }),
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: () => ({
          '&.Mui-selected': {
            backgroundColor: semanticTheme.dark.button.secondary.hover,
            borderRadius: 4,
          },

          '&.Mui-selected:hover': {
            backgroundColor: semanticTheme.dark.button.secondary.hover,
            borderRadius: 4,
          },
        }),
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: () => ({
          backgroundColor: semanticTheme.dark.surface.bg2,
          backgroundImage: 'none',
        }),
        listbox: {
          padding: 8,
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: semanticTheme.dark.button.secondary.hover,
            borderRadius: 4,
          },

          '& .MuiAutocomplete-option[aria-selected="true"]:hover': {
            backgroundColor: semanticTheme.dark.button.secondary.hover,
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.common.white,
          },

          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.text.primary,
          },

          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
          },
        }),
      },
    },

    MuiPickersTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `0.5px solid ${semanticTheme.dark.outline.medium}`,
          boxShadow: '0 1px 6px 0 #0000001F',
          borderRadius: '8px',
        },
      },
      defaultProps: {
        // The default props to change
        variant: 'outlined',
      },
    },
    MuiAlert: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: colors.primary[700],
          },
        },
      },
    },

    MuiAvatar: {
      styleOverrides: {
        root: {
          '&:has(.MuiAvatar-fallback)': {
            backgroundColor: semanticTheme.dark.avatars.default,
            color: semanticTheme.dark.avatars.contrastText,
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiDataGrid-toolbar': {
            borderBottom: 'none',
          },
          '& .MuiDataGrid-row--borderBottom .MuiDataGrid-columnHeader': {
            borderBottom: 'none',
          },
          border: `0.5px solid ${semanticTheme.dark.outline.medium}`,
          boxShadow: '0 1px 6px 0 #0000001F',
          borderRadius: '8px',
          backgroundColor: theme.palette.background.paper,
        }),
        columnHeaderTitle: {
          fontWeight: 'bold',
        },
        cell: {
          ':focus-within': {
            outline: 'none',
          },
        },
        filterForm: {
          gap: 8,
          alignItems: 'baseline',
        },
        detailPanel: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
        }),
      },
      defaultProps: {
        resizeThrottleMs: 8,
        slotProps: {
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton',
          },
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          border: `1px solid ${theme.palette.grey[800]}`,
        }),
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.grey[800],
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ theme }) => ({
          border: 'none',
          backgroundColor: theme.palette.drawerBlue.main,
          '--Paper-overlay': 'unset',
          backgroundImage: 'unset',
        }),
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-checked': {
            color: theme.palette.colors.info[400],
          },
        }),
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-checked': {
            color: theme.palette.colors.info[400],
          },
          '&.MuiCheckbox-indeterminate': {
            color: theme.palette.colors.info[400],
          },
        }),
      },
    },
  },
});
